import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import DashIcon from './DashIcon'

export interface ActionsMenuItem {
  label: string
  icon?: string
  onClick: () => void
  danger?: boolean
  disabled?: boolean
}

interface ActionsMenuProps {
  actions: ActionsMenuItem[]
}

// Rendu via portail vers document.body : un menu absolute/fixed resterait
// clippé par les wrappers `.tbl-scroll` (overflow-x: auto) des tables.
export default function ActionsMenu({ actions }: ActionsMenuProps) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState({ top: 0, right: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setCoords({ top: rect.bottom + 6, right: window.innerWidth - rect.right })
    }
    setOpen(o => !o)
  }

  useEffect(() => {
    if (!open) return

    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current?.contains(target) || btnRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    // Ferme au scroll (table ou page) plutôt que de recalculer la position en direct.
    const onScroll = () => setOpen(false)

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open])

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <DashIcon name="more" size={14} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="actions-menu"
          style={{ top: coords.top, right: coords.right }}
        >
          {actions.map(a => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              disabled={a.disabled}
              className={`actions-menu-item${a.danger ? ' is-danger' : ''}`}
              onClick={() => { a.onClick(); setOpen(false) }}
            >
              {a.icon && <DashIcon name={a.icon} size={14} />}
              {a.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}
